<template>
  <v-container class="pt-3 pb-6 px-6">

    <!-- Floating Button -->
    <floating-button @click="save()" save :disabled="invalid" v-if="authUser"/>

    <!-- Page Header -->
    <page-header
      :title="isNew + ' Caja de Sensores'"
      icon="mdi-cube-outline"
      divider="-"
      :breadcrumbs="breadcrumbs.items"
      refresh
      v-on:refresh="loadSensorsBoxes()"
    ></page-header>

    <v-row align="center" justify="space-around">
      <v-col cols="12">
        <v-card>
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Content -->
          <v-card-text v-if="authUser">
            <v-container>
              <v-row align="center" justify="space-around">
                <!-- Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="sensors_box.name"
                    :error-messages="nameErrors"
                    label="Nombre"
                    outlined
                    counter="32"
                    maxlength="32"
                    @blur="$v.sensors_box.name.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- MAC Address -->
                <v-col cols="4">
                  <v-text-field
                    v-model="sensors_box.mac_address"
                    :error-messages="macAddressErrors"
                    v-mask="'XX:XX:XX:XX:XX:XX:XX'"
                    label="Dirección MAC"
                    outlined
                    counter="20"
                    maxlength="20"
                    @blur="$v.sensors_box.mac_address.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- State -->
                <v-col cols="4">
                  <v-select
                    v-model="sensors_box.is_active"
                    :error-messages="stateErrors"
                    :items="state"
                    label="Estado"
                    outlined
                    @blur="$v.sensors_box.is_active.$touch()"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        style="cursor: pointer"
                        :color="data.item.value ? 'primary' : 'deep-orange'"
                        :class="{'white--text': !data.item.value}"
                      >{{ data.item.text }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
  </v-container>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import PageHeader from "@/components/PageHeader";
import { mask } from 'vue-the-mask'
import { validationMixin } from 'vuelidate';
import { required, macAddress } from 'vuelidate/lib/validators';
import { SensorsBoxesRepository } from "@/api/Repositories";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    PageHeader
  },
  directives: { mask },
  mixins: [validationMixin],
  validations: {
    sensors_box: {
      name: { required },
      mac_address: { required, macAddress: macAddress(':') },
      is_active: { required }
    }
  },
  data: () => ({
    authUser: false,
    isLoadingData: false,
    oldName: '',
    oldMAC: '',
    sensors_box: {
      name: '',
      mac_address: '',
      is_active: ''
    },
    state: [
      { text: 'Activo', value: true },
      { text: 'Inactivo', value: false }
    ],
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Cajas de Sensores", disabled: true }
      ]
    },
  }),
  computed: {
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    isNew () {
      return this.$route.params.id ? 'Modificar' : 'Añadir'
    },
    invalid () {
      return this.$v.$invalid && this.$v.$dirty
    },
    sensorsBoxes () {
      return this.$store.state.sensors_boxes.sensors_boxes
    },
    uniqueName () {
      if (this.sensors_box.name) {
        if (this.$route.params.id) {
          let nameExist = this.sensorsBoxes.some(element => {
            return element.name.trim() === this.sensors_box.name.trim()
            && element.name.trim() !== this.oldName.trim()
          })
          return !nameExist
        } else {
          let nameExist = this.sensorsBoxes.some(element => {
            return element.name.trim() === this.sensors_box.name.trim()
          })
          return !nameExist
        }
      } else {
        return true
      }
    },
    uniqueMacAddress () {
      if (this.sensors_box.mac_address) {
        if (this.$route.params.id) {
          let macAddressExist = this.sensorsBoxes.some(element => {
            return element.mac_address === this.sensors_box.mac_address
            && element.mac_address !== this.oldMAC
          })
          return !macAddressExist
        } else {
          let macAddressExist = this.sensorsBoxes.some(element => {
            return element.mac_address === this.sensors_box.mac_address
          })
          return !macAddressExist
        }
      } else {
        return true
      }
    },
    nameErrors () {
      const errors = []
      if (!this.$v.sensors_box.name.$dirty) return errors
      !this.$v.sensors_box.name.required && errors.push('Obligatorio.')
      !this.uniqueName && errors.push('Caja ya existe.')
      return errors
    },
    macAddressErrors() {
      const errors = [];
      if (!this.$v.sensors_box.mac_address.$dirty) return errors;
      !this.$v.sensors_box.mac_address.required && errors.push("Obligatorio.");
      !this.$v.sensors_box.mac_address.macAddress && errors.push("MAC no valida.");
      !this.uniqueMacAddress && errors.push('MAC ya existe.')
      return errors;
    },
    stateErrors () {
      const errors = []
      if (!this.$v.sensors_box.is_active.$dirty) return errors
      !this.$v.sensors_box.is_active.required && errors.push('Obligatorio.')
      return errors
    }
  },
  methods: {
    loadSensorsBoxes () {
      this.isLoadingData = true
      this.$store.dispatch('sensors_boxes/getSensorsBoxes')
        .then(() => {
          this.getSensorsBox()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando las cajas de sensores."
            Util.showErrorToast(this, error, errorToastMessage);
            this.sensorsBoxList();
          }
        })
    },
    getSensorsBox () {
      var id = this.$route.params.id
      if (id) {
        SensorsBoxesRepository.get(id)
          .then(response => {
            var data = response.data.data
            
            this.sensors_box = data
            this.oldName = data.name
            this.oldMAC = data.mac_address
          })
          .catch(error => {
            if (error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "Ha ocurrido un error cargando la caja de sensores."
              Util.showErrorToast(this, error, errorToastMessage);
            }
            this.sensorsBoxList();
          })
      } else {
        this.sensors_box = {
          name: '',
          mac_address: '',
          is_active: ''
        }
      }
      this.$v.$reset()
      this.authUser = true
      this.isLoadingData = false
    },
    sensorsBoxList () {
      this.$router.push({
        name: "sensors-list"
      });
    },
    save () {
      this.$route.params.id ? this.editSensorsBox() : this.createSensorsBox()
    },
    createSensorsBox () {
      this.$v.$touch()
      if (!this.$v.$invalid && this.uniqueName) {
        this.loading = true
        this.sensors_box.sensors_box_data = []
        this.sensors_box.sensors_box_image = []
        this.$store.dispatch('sensors_boxes/createSensorsBox', this.sensors_box)
          .then(response => {
            console.log(response)
            this.$toast("Caja de sensores creada correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.sensorsBoxList()
          })
          .catch(error => {
            this.$toast("Ha ocurrido un error creando la caja de sensores.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            console.log(error);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    editSensorsBox () {
      this.$v.$touch()
      if (!this.$v.$invalid && this.uniqueName) {
        this.loading = true
        this.$store.dispatch('sensors_boxes/editSensorsBox', this.sensors_box)
          .then(() => {
            this.$toast("Caja de sensores modificada correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.sensorsBoxList()
          })
          .catch(error => {
            this.$toast("Ha ocurrido un error modificando la caja de sensores.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            console.log(error);
          })
          .finally(() => {
            this.loading = false
          })
      }
    }
  },
  created () {
    this.loadSensorsBoxes()
  }
}
</script>
